import React, { useCallback, useState, createContext, useContext } from 'react';
import { sendSpacesMessage, RenderableResource } from './spaces';
import { useActivityFeed } from './activityFeed';
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  resources?: RenderableResource[];
  timestamp: Date;
}
export interface ChatContext {
  accountId: string;
  accountName: string;
  section: 'overview' | 'people' | 'orders' | 'assets' | 'analytics' | 'spaces';
  metadata?: Record<string, any>;
}
interface ChatSession {
  sessionId: string;
  messages: ChatMessage[];
  context: ChatContext;
}
interface ChatContextType {
  getSession: (accountId: string) => ChatSession;
  addMessage: (accountId: string, message: ChatMessage) => void;
  sendMessage: (accountId: string, apiEndpoint: string, sessionId: string, text: string) => Promise<void>;
  updateContext: (accountId: string, context: Partial<ChatContext>) => void;
  clearSession: (accountId: string) => void;
  isLoading: (accountId: string) => boolean;
}
const ChatContextContext = createContext<ChatContextType | undefined>(undefined);
export function ChatProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [sessions, setSessions] = useState<Record<string, ChatSession>>({});
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const getSession = useCallback((accountId: string): ChatSession => {
    if (!sessions[accountId]) {
      return {
        sessionId: accountId,
        messages: [],
        context: {
          accountId,
          accountName: '',
          section: 'overview'
        }
      };
    }
    return sessions[accountId];
  }, [sessions]);
  const addMessage = useCallback((accountId: string, message: ChatMessage) => {
    setSessions(prev => {
      const session = prev[accountId] || {
        sessionId: accountId,
        messages: [],
        context: {
          accountId,
          accountName: '',
          section: 'overview'
        }
      };
      return {
        ...prev,
        [accountId]: {
          ...session,
          messages: [...session.messages, message]
        }
      };
    });
  }, []);
  const updateContext = useCallback((accountId: string, contextUpdate: Partial<ChatContext>) => {
    setSessions(prev => {
      const session = prev[accountId] || {
        sessionId: accountId,
        messages: [],
        context: {
          accountId,
          accountName: '',
          section: 'overview'
        }
      };
      const newContext = {
        ...session.context,
        ...contextUpdate
      };
      // Update context without adding system messages
      return {
        ...prev,
        [accountId]: {
          ...session,
          context: newContext
        }
      };
    });
  }, []);
  const sendMessage = useCallback(async (accountId: string, apiEndpoint: string, sessionId: string, text: string) => {
    // Guard: check if API endpoint is valid
    if (!apiEndpoint || apiEndpoint.trim() === '') {
      console.error('No API endpoint configured for account:', accountId);
      const errorMessage: ChatMessage = {
        id: `msg-${Date.now()}-${Math.random()}`,
        role: 'assistant',
        content: 'Chat is not configured for this account. Please contact your administrator.',
        timestamp: new Date()
      };
      addMessage(accountId, errorMessage);
      return;
    }
    const session = getSession(accountId);
    const context = session.context;
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random()}`,
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    addMessage(accountId, userMessage);
    setLoadingKeys(prev => new Set([...prev, accountId]));
    try {
      // Build context-aware message
      const contextPrefix = buildContextPrefix(context);
      const contextualText = contextPrefix ? `${contextPrefix}\n\n${text}` : text;
      console.log('Sending message to:', apiEndpoint);
      console.log('Session ID:', sessionId);
      console.log('Message:', contextualText);
      const data = await sendSpacesMessage({
        apiEndpoint,
        sessionId,
        text: contextualText
      });
      console.log('Received response:', data);
      const reply = data?.text?.content || 'No response received';
      const resources = data?.text?.resources?.map(r => r.type === 'document' ? {
        kind: 'doc' as const,
        title: r.document.title,
        id: r.document.id,
        read: r.document.read
      } : {
        kind: 'url' as const,
        title: r.url.title,
        href: r.url.url,
        favicon: r.url.favicon
      }) || [];
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}-${Math.random()}`,
        role: 'assistant',
        content: reply,
        resources: resources.length > 0 ? resources : undefined,
        timestamp: new Date()
      };
      addMessage(accountId, assistantMessage);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: ChatMessage = {
        id: `msg-${Date.now()}-${Math.random()}`,
        role: 'assistant',
        content: 'There was a problem reaching the chatbot. Please try again or contact support if the issue persists.',
        timestamp: new Date()
      };
      addMessage(accountId, errorMessage);
    } finally {
      setLoadingKeys(prev => {
        const next = new Set([...prev]);
        next.delete(accountId);
        return next;
      });
    }
  }, [addMessage, getSession]);
  const clearSession = useCallback((accountId: string) => {
    setSessions(prev => {
      const next = {
        ...prev
      };
      delete next[accountId];
      return next;
    });
  }, []);
  const isLoading = useCallback((accountId: string) => {
    return loadingKeys.has(accountId);
  }, [loadingKeys]);
  return <ChatContextContext.Provider value={{
    getSession,
    addMessage,
    sendMessage,
    updateContext,
    clearSession,
    isLoading
  }} data-id="element-1540">
      {children}
    </ChatContextContext.Provider>;
}
export function useChat() {
  const context = useContext(ChatContextContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
}
/**
 * Build context prefix for API messages
 */
function buildContextPrefix(context: ChatContext): string {
  const sectionContext = {
    overview: 'viewing the account overview',
    people: 'viewing the People section',
    orders: 'viewing the Orders section',
    assets: 'viewing the Assets section',
    analytics: 'viewing the Analytics section',
    spaces: 'viewing Knowledge Spaces'
  };
  const parts = [`[Context: User is currently ${sectionContext[context.section]}`];
  if (context.accountName) {
    parts.push(`for ${context.accountName} account`);
  }
  if (context.metadata?.personName) {
    parts.push(`and asking about ${context.metadata.personName}`);
  }
  parts.push(']');
  return parts.join(' ');
}